import { PagedResult } from "../DTOs/Common/PagedResult"
import { DiggingLocationDTO } from "../DTOs/Production/DiggingLocationDTO"
import { PitOptionDTO } from "../DTOs/Production/PitOptionDTO"
import { TruckLoadDTO } from "../DTOs/Production/TruckLoadDTO"
import { PlantBucketOptionDTO } from "../DTOs/Production/PlantBucketOptionDTO"
import { ajaxController, formatDatesForTimeOnlyModel } from "./controller/AjaxController"
import { FlatGroupOption } from "../DTOs/Common/FlatGroupOption";
import { GetDiggingLocationByDiggingPatternIdDTO } from "../DTOs/Production/GetDiggingLocationByDiggingPatternIdDTO"
import BlastMasterIdsLookupDTO from "../DTOs/Production/BlastMasterIdsLookupDTO"
import { DateTime } from "luxon";
import { DataTableFilteredRequest } from "../components/DataTable/TableHook";
import { DoubleShift } from "../types/Shift";
import { GetDiggingSummaryPagedResult } from "../DTOs/Production/GetDiggingSummaryPagedResultDTO"
import { TruckLoadsPagedResult } from "../DTOs/Production/TruckLoadsPagedResult"
import { PagedRequest } from "../DTOs/Common/PagedRequest"
import { TruckLoadMutationResultDTO } from "../DTOs/Production/TruckLoadMutationResultDTO"

class DiggingAjax {
  private _ajaxController = ajaxController
  private _diggingDataEndpoint = "/Digging"

  // Production Locations
  public getLocationByDiggingPatternId(diggingPatternId: number) {
    return this._ajaxController.get<GetDiggingLocationByDiggingPatternIdDTO>(
      `${this._diggingDataEndpoint}/locations/${diggingPatternId}`,
      {}
    )
  }

  public getDiggerLocations(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    diggerId: number
  ) {
    return this._ajaxController.get<PagedResult<DiggingLocationDTO>>(
      `${this._diggingDataEndpoint}/locations/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}`,
      {}
    )
  }

  public updateDiggingLocation(dto: DiggingLocationDTO) {
    return this._ajaxController.put<DiggingLocationDTO>(
      `${this._diggingDataEndpoint}/locations`, dto, formatDatesForTimeOnlyModel
    )
  }

  public insertDiggingLocation(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    diggerId: number,
    location: DiggingLocationDTO
  ) {
    return this._ajaxController.post<DiggingLocationDTO, DiggingLocationDTO>(
      `${this._diggingDataEndpoint}/locations/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}`,
      location,
      formatDatesForTimeOnlyModel
    )
  }

  // Digging Pattern
  public deleteDiggingPattern(diggingPatternId: number) {
    return this._ajaxController.delete(
      `${this._diggingDataEndpoint}/deleteDiggingPattern/${diggingPatternId}`
    )
  }

  // Truck Loads
  public getTruckLoads(diggingPatternId: number, hour: number, request?: PagedRequest) {
    return this._ajaxController.get<TruckLoadsPagedResult>(
      `${this._diggingDataEndpoint}/truck-loads/${diggingPatternId}/${hour}`,
      request ?? {}
    );
  }

  public updateTruckLoad(dto: TruckLoadDTO) {
    return this._ajaxController.post<TruckLoadDTO, TruckLoadMutationResultDTO>(
      `${this._diggingDataEndpoint}/truck-loads/update`,
      dto
    )
  }

  public insertTruckLoad(dto: TruckLoadDTO) {
    return this._ajaxController.post<TruckLoadDTO, TruckLoadMutationResultDTO>(
      `${this._diggingDataEndpoint}/truck-loads/insert`,
      dto
    )
  }

  public deleteTruckLoad(loadingId: number) {
    return this._ajaxController.post<null, TruckLoadMutationResultDTO>(
      `${this._diggingDataEndpoint}/truck-loads/delete/${loadingId}`,
      null
    )
  }

  public copyPreviousHourLoads(diggingPatternId: number, hour: number) {
    return this._ajaxController.post<null, TruckLoadDTO[]>(
      `${this._diggingDataEndpoint}/truck-loads/copy/previous-hour/${diggingPatternId}/${hour}`,
      null
    )
  }

  public moveLoadingRecord(loadingId: number, newDiggingPatternId: number) {
    const data = { loadingId, newDiggingPatternId };

    return this._ajaxController.post<{ loadingId: number; newDiggingPatternId: number }, TruckLoadDTO[]>(
      `${this._diggingDataEndpoint}/truck-loads/move`,
      data
    )
  }

  public async sumTruckLoadsForHours(
    diggingPatternId: number,
    hours: number[],
    concurrency = 3
  ): Promise<Record<number, number>> {
    const result: Record<number, number> = {};
    let i = 0;

    while (i < hours.length) {
      const batch = hours.slice(i, i + concurrency);
      const sums = await Promise.all(batch.map(async h => {
        try {
          const resp = await this.getTruckLoads(diggingPatternId, h);
          const raw: any = resp;
          const items = Array.isArray(raw?.data?.items)
            ? raw.data.items
            : Array.isArray(raw?.items)
              ? raw.items
              : Array.isArray(raw?.data)
                ? raw.data
                : Array.isArray(raw)
                  ? raw
                  : [];
          return items.reduce((a: number, r: any) => a + (Number(r.loads ?? r.Loads) || 0), 0);
        } catch {
          return 0;
        }
      }));
      batch.forEach((h, idx) => { result[h] = sums[idx]; });
      i += concurrency;
      await new Promise(r => setTimeout(r, 30));
    }

    return result;
  }

  // Options
  public getLocationOptions(shiftDate: DateTime, dayOrNight: DoubleShift) {
    return this._ajaxController.get<PagedResult<PitOptionDTO>>(
      `${this._diggingDataEndpoint}/location-options/${shiftDate.toISODate()}/${dayOrNight[0]}`, {}
    )
  }

  public getBlastMaterialOptions() {
    return this._ajaxController.get<PagedResult<FlatGroupOption>>(
      `${this._diggingDataEndpoint}/material-options`,
      {}
    );
  }

  public getPlantBuckets(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    diggerId: number,
    patternBlastId: number,
    truckId: number
  ) {
    return this._ajaxController.get<PagedResult<PlantBucketOptionDTO>>(
      `${this._diggingDataEndpoint}/truck-buckets/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}/${patternBlastId}/${truckId}`,
      {}
    );
  }

  public getBlastmasterIds(shiftDate: DateTime, dayOrNight: DoubleShift) {
    return this._ajaxController.get<PagedResult<BlastMasterIdsLookupDTO>>(
      `${this._diggingDataEndpoint}/blastmaster-options/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      {}
    )
  }

  // Digging Loading Summary
  async getDiggingLoadingSummary(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    filters: DataTableFilteredRequest
  ) {
    return this._ajaxController.get<GetDiggingSummaryPagedResult>(
      `${this._diggingDataEndpoint}/summary/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      filters
    );
  }
}

export const diggingAjax = new DiggingAjax()
